import { Shield, ShieldCheck, ShieldAlert } from "lucide-react";
import type { AgentPermissions } from "@paperclipai/shared";
import { cn } from "@/lib/utils";

export type TrustPreset = "standard" | "low_trust_review" | "high_trust";

interface TrustPresetSectionProps {
  permissions: Partial<AgentPermissions>;
  onChange: (permissions: Partial<AgentPermissions>) => void;
  disabled?: boolean;
  companyId?: string | null;
  projectCandidates?: Array<{ id: string; label: string }>;
  issueCandidates?: Array<{ id: string; label: string }>;
  candidatesLoading?: boolean;
}

const PRESETS: Array<{
  key: TrustPreset;
  label: string;
  description: string;
  icon: typeof Shield;
}> = [
  {
    key: "standard",
    label: "Standard",
    description: "Agent operates within normal approval boundaries.",
    icon: Shield,
  },
  {
    key: "high_trust",
    label: "High Trust",
    description: "Agent can create sub-agents without approval.",
    icon: ShieldCheck,
  },
  {
    key: "low_trust_review",
    label: "Low Trust / Review",
    description: "All actions require explicit review.",
    icon: ShieldAlert,
  },
];

export function TrustPresetSection({
  permissions,
  onChange,
  disabled,
}: TrustPresetSectionProps) {
  const current = permissions.canCreateAgents
    ? "high_trust"
    : "standard";

  function selectPreset(preset: TrustPreset) {
    if (disabled) return;
    switch (preset) {
      case "high_trust":
        onChange({ canCreateAgents: true });
        break;
      case "low_trust_review":
        onChange({ canCreateAgents: false });
        break;
      case "standard":
      default:
        onChange({ canCreateAgents: false });
        break;
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-medium">Trust preset</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Define what this agent is allowed to do autonomously.
        </p>
      </div>
      <div className="space-y-2">
        {PRESETS.map((preset) => {
          const Icon = preset.icon;
          const active = current === preset.key;
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => selectPreset(preset.key)}
              disabled={disabled}
              className={cn(
                "w-full flex items-start gap-3 border p-3 text-left transition-colors",
                active
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-accent/50",
                disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 mt-0.5 shrink-0",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              />
              <div className="space-y-0.5">
                <div className="text-sm font-medium">{preset.label}</div>
                <div className="text-xs text-muted-foreground">
                  {preset.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
